import { useEffect, useRef, useState } from "react";
import {
  useLiveFeed,
  type DynoSamplesIncoming,
  type DynoTuningIncoming,
  type DynoDemandIncoming,
  type DynoPqIncoming,
  type DynoSigIncoming,
  type SystemRegistryIncoming,
} from "@/stores/liveFeed";
import { useEventQueue } from "@/stores/eventQueue";
import type { PqEvent } from "@/lib/types";
import { record as recordPerf, recordChange } from "@/lib/realtimePerf";

// A2.2 — dyno-pwa MQTT consumer hook backed by the EMQX broker.
//
// v2 composer (A2.2b — this commit). Subscribes to four EMQX topic
// families and dispatches each into the right liveFeed action:
//
//   ovr/+/dyno_samples       → liveFeed.applyDynoSamples
//   ovr/dyno/tuning/+        → liveFeed.applyDynoTuning   (retained, A1.3)
//   ovr/dyno/demand/+        → liveFeed.applyDynoDemand   (retained, A1.4)
//   ovr/dyno/pq_event/+      → eventQueue.ingest           (A1.5)
//
// Why composer-pattern: each topic carries one bounded concern (live
// scalars / tuning state / demand resolution / PQ events). Joining
// them client-side lets cloud-api stop building the cloud-aggregated
// dyno_snapshot at all eventually (B2). Retained topics give us
// instant state on connect (no warm-up gap).
//
// registry/system/+ NOT subscribed here — service static fields
// (service_name, capacity, node_id, etc.) still come from /api/services.
// The plan calls out that as a future composer-v3 win.
//
// Returns: { mqttConnecting, mqttConnected, mqttFailed }

/**
 * Detect per-(system, field) value changes in a dyno_samples batch and record
 * them via realtimePerf.recordChange. The fields tracked are the ones the
 * service tile actually displays — p (active power), i_peak (max-of-legs
 * amps), demand_w. The edge_ts used for each change is the LAST sample's ts
 * in the batch (the timestamp of the newest data point that carried the
 * changed value). recordChange's own previous-value gate filters out
 * unchanged values so steady-state samples don't pollute the histogram.
 *
 * Cross-tab use: open ?mqtt_v2=1 and default tabs side by side; call
 * window.__realtimePerf.summary() in each. The `changes` rows show the
 * operator-felt latency per path; subtract MQTT-p50 from WS-p50 to get
 * "MQTT shows changes N ms sooner".
 */
export function recordSampleChanges(
  source: "mqtt" | "ws",
  topic: string,
  systems: DynoSamplesIncoming[],
): void {
  for (const s of systems) {
    if (!s.system_id || !Array.isArray(s.ts) || s.ts.length === 0) continue;
    const lastIdx = s.ts.length - 1;
    const edgeTs = s.ts[lastIdx];
    if (typeof edgeTs !== "number" || !Number.isFinite(edgeTs)) continue;

    const lastOfCol = (col: unknown): number | null => {
      if (!Array.isArray(col) || col.length === 0) return null;
      // Walk backwards to find the most-recent non-null sample in case the
      // last index is null (edge can emit ragged batches when a phase is off).
      for (let i = col.length - 1; i >= 0; i -= 1) {
        const v = col[i];
        if (v != null) return v as number;
      }
      return null;
    };

    // p — active power total
    const p = lastOfCol(s["active_power"]);
    recordChange(source, topic, `${s.system_id}:p`, p, edgeTs);

    // i_peak — max of L1/L2/L3 current
    const i1 = lastOfCol(s["current_l1"]);
    const i2 = lastOfCol(s["current_l2"]);
    const i3 = lastOfCol(s["current_l3"]);
    if (i1 != null || i2 != null || i3 != null) {
      const iPeak = Math.max(i1 ?? -Infinity, i2 ?? -Infinity, i3 ?? -Infinity);
      if (Number.isFinite(iPeak)) {
        recordChange(source, topic, `${s.system_id}:i_peak`, iPeak, edgeTs);
      }
    }
  }
}

const DISCONNECT_GRACE_MS = 10_000;
const TOKEN_REFRESH_LEAD_S = 300;
const FAILURE_THRESHOLD = 3;
const FAILED_AFTER_MS = 10_000;

export function isMqttV2Enabled(): boolean {
  // Cutover (2026-06-16): MQTT (EMQX) is now the DEFAULT realtime transport for
  // dyno-pwa — interim B1, done early for testing since no live event is active.
  // Explicit opt-out only: ?mqtt_v2=0 (URL) or localStorage.mqtt_v2="0". The
  // Dashboard gate still auto-falls back to useDynoWs when MQTT trips its
  // failure threshold, so this remains runtime-reversible without a redeploy.
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("mqtt_v2") === "1") return true;
    if (url.searchParams.get("mqtt_v2") === "0") return false;
    return window.localStorage.getItem("mqtt_v2") !== "0";
  } catch {
    return true;
  }
}

interface TokenResponse {
  url: string;
  username: string;
  password: string;
  kind: "admin" | "portal" | "service";
  systems?: string[] | null;
  exp?: number;
}

async function fetchMqttToken(): Promise<TokenResponse> {
  const res = await fetch("/api/mqtt-token", {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`mqtt-token HTTP ${res.status}`);
  }
  return (await res.json()) as TokenResponse;
}

export interface UseDynoMqttResult {
  mqttConnecting: boolean;
  mqttConnected: boolean;
  mqttFailed: boolean;
}

export function useDynoMqtt({ enabled = true }: { enabled?: boolean } = {}): UseDynoMqttResult {
  const applyDynoSamples = useLiveFeed((s) => s.applyDynoSamples);
  const applyDynoTuning = useLiveFeed((s) => s.applyDynoTuning);
  const applyDynoDemand = useLiveFeed((s) => s.applyDynoDemand);
  const applyDynoPq = useLiveFeed((s) => s.applyDynoPq);
  const applyDynoSig = useLiveFeed((s) => s.applyDynoSig);
  const applySystemRegistry = useLiveFeed((s) => s.applySystemRegistry);
  const setConnected = useLiveFeed((s) => s.setConnected);
  const ingestEvent = useEventQueue((s) => s.ingest);

  const [mqttConnecting, setMqttConnecting] = useState(false);
  const [mqttConnected, setMqttConnected] = useState(false);
  const [mqttFailed, setMqttFailed] = useState(false);

  const disconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasConnectedRef = useRef(false);
  const failureCountRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      // Reset state on disable so the chip doesn't lie about path.
      setMqttConnecting(false);
      setMqttConnected(false);
      setMqttFailed(false);
      return;
    }

    let client: import("mqtt").MqttClient | null = null;
    let cancelled = false;
    setMqttConnecting(true);

    const triggerFailed = (reason: string) => {
      if (cancelled || hasConnectedRef.current) return;
      // eslint-disable-next-line no-console
      console.warn(`[Dyno MQTT v2] failover trigger: ${reason}`);
      setMqttFailed(true);
      setMqttConnecting(false);
      setConnected(false);
      if (client) {
        try { client.end(true); } catch { /* ignore */ }
        client = null;
      }
    };

    watchdogTimerRef.current = setTimeout(() => {
      triggerFailed(`no broker response within ${FAILED_AFTER_MS}ms`);
    }, FAILED_AFTER_MS);

    const markDisconnected = () => {
      if (disconnectTimerRef.current) return;
      if (!hasConnectedRef.current) return;
      disconnectTimerRef.current = setTimeout(() => {
        disconnectTimerRef.current = null;
        setMqttConnected(false);
        setConnected(false);
      }, DISCONNECT_GRACE_MS);
    };

    const markConnected = () => {
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      if (watchdogTimerRef.current) {
        clearTimeout(watchdogTimerRef.current);
        watchdogTimerRef.current = null;
      }
      hasConnectedRef.current = true;
      failureCountRef.current = 0;
      setMqttConnecting(false);
      setMqttConnected(true);
      setMqttFailed(false);
      setConnected(true);
    };

    const scheduleTokenRefresh = (expSecs: number) => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      const nowSecs = Math.floor(Date.now() / 1000);
      const refreshIn = Math.max(60, expSecs - nowSecs - TOKEN_REFRESH_LEAD_S);
      refreshTimerRef.current = setTimeout(() => {
        if (cancelled) return;
        connect().catch((err) => {
          // eslint-disable-next-line no-console
          console.error("[Dyno MQTT] Token refresh failed:", err);
          markDisconnected();
        });
      }, refreshIn * 1000);
    };

    const connect = async () => {
      if (client) {
        try { client.end(true); } catch { /* ignore */ }
        client = null;
      }

      let tok: TokenResponse;
      try {
        tok = await fetchMqttToken();
      } catch (err) {
        failureCountRef.current += 1;
        // eslint-disable-next-line no-console
        console.error(
          `[Dyno MQTT v2] token fetch failed (${failureCountRef.current}/${FAILURE_THRESHOLD}):`,
          err,
        );
        if (failureCountRef.current >= FAILURE_THRESHOLD) {
          triggerFailed(`${FAILURE_THRESHOLD} consecutive token-fetch failures`);
        }
        return;
      }
      if (cancelled) return;

      const mqttModule = await import("mqtt");
      const mqtt = mqttModule.default || mqttModule;
      if (cancelled) return;

      client = mqtt.connect(tok.url, {
        username: tok.username,
        password: tok.password,
        keepalive: 30,
        reconnectPeriod: 5000,
        connectTimeout: 10000,
      });

      client.on("connect", () => {
        markConnected();
        // Wildcard subscribes for admin/service. Portal users would need
        // per-system enumeration here too but the dyno/tuning/demand/pq_event
        // topics are admin/service surfaces (PQ tuning is operator-driven,
        // portal users don't see it) so we just no-op for portal kind.
        if (tok.systems == null) {
          client?.subscribe("ovr/+/dyno_samples", { qos: 0 });
          client?.subscribe("ovr/dyno/tuning/+", { qos: 0 });
          client?.subscribe("ovr/dyno/demand/+", { qos: 0 });
          client?.subscribe("ovr/dyno/pq/+", { qos: 0 });
          client?.subscribe("ovr/dyno/pq_event/+", { qos: 0 });
          // C1 + C2 — retained identity (meter_model, live node_id,
          // location) per system. Retained replay on subscribe gives us
          // the full inventory immediately; subsequent updates fire on
          // change only via the publisher's content-hash dedup.
          client?.subscribe("ovr/registry/system/+", { qos: 0 });
          // C3 — retained tile staleness signal (ok / stale). Cloud-side
          // 10s tick publishes change-only via content-hash dedup.
          client?.subscribe("ovr/dyno/sig/+", { qos: 0 });
        } else if (tok.systems.length > 0) {
          // Portal scope: subscribe to per-system dyno_samples only — the
          // edge publishes per-node so this is still wildcarded and
          // payload-filtered, same v1 hack as before. tuning/demand/pq_event
          // are admin surfaces; portal doesn't render them.
          client?.subscribe("ovr/+/dyno_samples", { qos: 0 });
        }
        // eslint-disable-next-line no-console
        console.info(
          `[Dyno MQTT v2] connected as ${tok.username} (${tok.kind}); composer=${tok.systems == null ? "full" : "samples-only"}`,
        );
      });

      client.on("message", (topic, message) => {
        try {
          const payload = JSON.parse(message.toString());
          // Topic-dispatch. Each family has its own ts semantics — only the
          // fast-cadence dyno_samples gets perf recording (the slow retained
          // topics would dominate the ring otherwise).
          if (topic.endsWith("/dyno_samples")) {
            const p = payload as { systems?: DynoSamplesIncoming[]; ts?: number };
            if (typeof p.ts === "number") recordPerf("mqtt", topic, p.ts);
            if (Array.isArray(p.systems) && p.systems.length > 0) {
              applyDynoSamples(p.systems);
              recordSampleChanges("mqtt", topic, p.systems);
            }
            return;
          }
          if (topic.startsWith("ovr/dyno/tuning/")) {
            applyDynoTuning(payload as DynoTuningIncoming);
            return;
          }
          if (topic.startsWith("ovr/dyno/demand/")) {
            applyDynoDemand(payload as DynoDemandIncoming);
            return;
          }
          // ovr/dyno/pq/<sid> (retained) — live THD/TDD/K-factor scalars.
          // Checked before pq_event/ is irrelevant (different segment), but
          // note "ovr/dyno/pq/" does NOT prefix-match "ovr/dyno/pq_event/".
          if (topic.startsWith("ovr/dyno/pq/")) {
            applyDynoPq(payload as DynoPqIncoming);
            return;
          }
          if (topic.startsWith("ovr/registry/system/")) {
            applySystemRegistry(payload as SystemRegistryIncoming);
            return;
          }
          if (topic.startsWith("ovr/dyno/sig/")) {
            applyDynoSig(payload as DynoSigIncoming);
            return;
          }
          if (topic.startsWith("ovr/dyno/pq_event/")) {
            // Payload is the PQ event body merged with kind: "keep" | "drop"
            // (see emqx_publisher.publish_pq_event). Map kind → decision so
            // eventQueue.ingest's supersession ranking works the same as WS.
            const p = payload as Partial<PqEvent> & {
              event_id?: string;
              kind?: "keep" | "drop";
            };
            if (!p.event_id) return;
            ingestEvent({
              ...(p as PqEvent),
              decision: p.kind === "drop" ? "drop" : "keep",
            } as PqEvent);
            return;
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[Dyno MQTT] Message parse error:", err);
        }
      });

      client.on("error", markDisconnected);
      client.on("close", markDisconnected);
      client.on("offline", markDisconnected);

      if (tok.exp) scheduleTokenRefresh(tok.exp);
    };

    connect();

    return () => {
      cancelled = true;
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      if (watchdogTimerRef.current) {
        clearTimeout(watchdogTimerRef.current);
        watchdogTimerRef.current = null;
      }
      if (client) {
        try { client.end(true); } catch { /* ignore */ }
        client = null;
      }
    };
  }, [enabled, applyDynoSamples, applyDynoTuning, applyDynoDemand, applyDynoSig, applySystemRegistry, ingestEvent, setConnected]);

  return { mqttConnecting, mqttConnected, mqttFailed };
}
